import React, { useState, useEffect } from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { handleApiError } from '@utils/apiErrorHandler';
import { formatDate } from '@utils/dateTime';
import { Table, Tag, Button, Empty, Spin, Progress } from 'antd';
import { useTranslation } from 'react-i18next';

import POInvoiceDrawer from './POInvoiceDrawer';

const INVOICE_STATUS_COLORS = {
  unpaid: 'red',
  partial_paid: 'orange',
  paid: 'green',
  canceled: 'default',
};

export default function POInvoicesPanel({ po, service }) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await service.getInvoices(po.id);
      if (res.success) {
        setInvoices(res.data || []);
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [po.id]);

  const handleRowClick = (record) => {
    setSelectedInvoiceId(record.id);
    setDrawerMode('view');
  };

  const handleCreateClick = () => {
    setSelectedInvoiceId(null);
    setDrawerMode('create');
  };

  const handleDrawerClose = () => {
    setSelectedInvoiceId(null);
    setDrawerMode(null);
  };

  const handleUpdate = () => {
    loadInvoices();
  };

  const selectedInvoice = invoices.find((inv) => inv.id === selectedInvoiceId);

  const isOverdue = (invoice) => {
    return invoice.due_date &&
      invoice.due_date < new Date().toISOString().slice(0, 10) &&
      invoice.status !== 'paid' &&
      invoice.status !== 'canceled';
  };

  const columns = [
    {
      title: t('models.purchase_order.invoice.number'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text, record) => (
        <span style={{ fontWeight: selectedInvoiceId === record.id ? 600 : 400 }}>{text}</span>
      ),
    },
    {
      title: t('models.purchase_order.invoice.total_amount'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (val) => parseFloat(val).toLocaleString(),
    },
    {
      title: t('models.purchase_order.invoice.due_date'),
      dataIndex: 'due_date',
      key: 'due_date',
      render: (val, record) => val ? (
        <span style={{ color: isOverdue(record) ? '#ff4d4f' : undefined }}>
          {formatDate(val)}
          {isOverdue(record) && <WarningOutlined style={{ marginLeft: 4 }} />}
        </span>
      ) : '-',
    },
    {
      title: t('models.purchase_order.invoice.paid_amount'),
      key: 'progress',
      render: (_, record) => {
        const pct = record.total_amount > 0
          ? Math.round((parseFloat(record.paid_amount || 0) / parseFloat(record.total_amount)) * 100)
          : 0;
        return (
          <Progress
            percent={pct}
            size="small"
            showInfo
            format={(p) => `${p}%`}
            strokeColor={pct >= 100 ? '#52c41a' : '#d48806'}
            style={{ minWidth: 80 }}
          />
        );
      },
    },
    {
      title: t('models.purchase_order.invoice.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={INVOICE_STATUS_COLORS[status]}>
          {t(`models.purchase_order.invoice.statuses.${status}`)}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', minHeight: 300 }}>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ fontSize: 12, color: '#888' }}>
            {t('models.purchase_order.invoice.count', { count: invoices.length })}
          </span>
          <Button size="small" type="primary" onClick={handleCreateClick}>
            + {t('models.purchase_order.invoice.create')}
          </Button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : invoices.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 40 }} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={invoices}
            pagination={false}
            size="small"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: {
                cursor: 'pointer',
                background: selectedInvoiceId === record.id ? '#e6f4ff' : undefined,
              },
            })}
          />
        )}
      </div>

      {drawerMode && (
        <POInvoiceDrawer
          invoice={selectedInvoice}
          po={po}
          service={service}
          onClose={handleDrawerClose}
          onUpdate={handleUpdate}
          mode={drawerMode}
        />
      )}
    </div>
  );
}
